import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';

import CreateNoteTemplateModal from './CreateNoteTemplate';
import NoteTemplateGroup from './NoteTemplateGroup';
import NoteTemplate from './NoteTemplate';
import CreateFolderModal from './NoteTemplate.CreateFolderModal';
import './note-templates.scss';

const NoteTemplates = ({
  folders,
  templates,
  createNoteTemplate,
  createNoteTemplateFolder,
  updateNoteTemplateFolder,
  deleteNoteTemplateFolder,
  reorderNoteTemplateFolders,
  reorderNoteTemplates,
}) => {
  const [itemTemplates, setItemTemplates] = useState(templates);
  const [draggingTemplate, setDraggingTemplate] = useState(null);
  const [isCreateModalOpened, setCreateModalOpened] = useState(false);
  const [selectedTemplateFolder, setTemplateFolderSelected] = useState(-1);
  const [templateModalOpened, setTemplateModalOpened] = useState(false);

  useEffect(() => {
    setItemTemplates(templates);
  }, [templates, folders]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 10 },
    }),
  );

  const sortFn = (asc = false) => {
    return (a, b) => {
      if (a.position > b.position) return asc ? 1 : -1;
      if (a.position < b.position) return asc ? -1 : 1;
      return 0;
    };
  };

  const onDragStart = ({ active }) => {
    setDraggingTemplate(itemTemplates.find(i => i.id === active.id));
  };

  const handleDragOver = ({ active, over }) => {
    const overId = over?.id;
    const activeContainer = active.data.current.sortable?.containerId;
    const overContainer = over?.data.current?.sortable?.containerId || overId;

    // over.data.current will be null if dragging into a collapse group.
    // To prevent weird jumpiness when dragging an item into a collapsed group.
    if (!over.data.current) {
      setDraggingTemplate(itemTemplates.find(i => i.id === active.id));
      return;
    }

    if (activeContainer !== overContainer) {
      const overItem = itemTemplates.find((i) => i.id === overId);
      const folderId = overItem ? overItem.noteTemplateFolderId : overId;
      if (overItem || folderId) {
        const maxOrder = Math.max(...(itemTemplates
          .filter(el => el.noteTemplateFolderId === folderId)
          .map(el => el.position)));
        setItemTemplates(itemTemplates
          .map(el => el.id === active.id ? { ...el, noteTemplateFolderId: folderId, position: maxOrder + 1 } : el));
      }
    }
  };

  const handleDragEnd = ({ active, over }) => {
    const overId = over?.id;
    if (!overId) {
      return;
    }

    let position;
    let folderId;
    let index = 0;

    // drop over note
    if (over.data.current) {
      position = itemTemplates.find((item) => item.id === over.id)?.position;
      folderId = itemTemplates.find((item) => item.id === active.id)?.noteTemplateFolderId;
      setItemTemplates(
        [...itemTemplates]
          .map((item) => (item.id === active.id ? { ...item, position } : item))
          .sort(sortFn(true))
          .map((item) => {
            let sortedItem = item;
            if (item.noteTemplateFolderId === folderId) {
              index = position === index ? index += 1 : index;
              if (item.id !== active.id) {
                sortedItem = { ...item, position: index };
                index += 1;
              }
            }
            return sortedItem;
          }));
    } else {
      // drop over group
      position = 0;
      folderId = over.id;
      setItemTemplates([...itemTemplates]
        .map(item => item.id === active.id ? { ...item, position, noteTemplateFolderId: folderId } : item));
    }
    reorderNoteTemplates({ folderId, id: active.id, position });
    setDraggingTemplate(null);
  };

  const createNewTemplate = (id) => {
    setTemplateFolderSelected(id);
    setCreateModalOpened(true);
  };

  const handleCreateNote = (noteBody) => {
    const templateBody = {
      ...noteBody,
      name: noteBody.title,
      noteTemplateFolderId: selectedTemplateFolder,
    };
    createNoteTemplate(templateBody);
  };

  return (
    <>
      <div className="content-container notes-container note-templates">
        <section className="page-title page-title--actions">
          <h2 className="title heading-section">Note Templates</h2>
          <div className="page-title__actions">
            <button
              className={clsx(
              'button',
              'button--md',
              'button--action',
            )}
              onClick={() => setTemplateModalOpened(true)}
            >
              Add New Folder
            </button>
          </div>
        </section>
        <p className="sub-title">
          Note templates are a great way to streamline your process by creating templates for all the notes
          you typically share with clients or use for internal purposes. Start by adding folders below,
          then create as many note templates as you want. Learn more{' '}
          <a
            href="https://help.aisleplanner.com/en/collections/72585-note-templates"
            target="_blank"
            rel="noreferrer"
          >
            here
          </a>
          .
        </p>
        <section className="page-body">
          <DndContext
            sensors={sensors}
            onDragStart={onDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            collisionDetection={closestCenter}
          >
            <div className="group-list">
              {folders?.map((folder, index) => {
              return (
                <div key={folder.id}>
                  <NoteTemplateGroup
                    key={folder.id}
                    id={folder.id}
                    groupIndex={index}
                    order={folder.position}
                    templates={itemTemplates
                      .filter((item) => item.noteTemplateFolderId === folder.id)
                      .sort(sortFn(true))
                    }
                    name={folder.name}
                    isLast={index === folders.length - 1}
                    isDragging={draggingTemplate?.noteTemplateFolderId === folder.id}
                    updateNoteTemplateFolder={updateNoteTemplateFolder}
                    deleteNoteTemplateFolder={deleteNoteTemplateFolder}
                    reorderNoteTemplateFolders={reorderNoteTemplateFolders}
                    ableToDelete={folders?.length > 1}
                  />
                  <button
                    className={clsx('button--unstyled', 'gold', 'button--add')}
                    onClick={() => createNewTemplate(folder.id)}
                  >
                    Add a Template
                  </button>
                </div>
              );
            })}
            </div>
            <DragOverlay
              dropAnimation={null}
            >
              {draggingTemplate ? (
                <NoteTemplate
                  {...itemTemplates.find(element => element.id === draggingTemplate.id)}
                  style={{
                    cursor: 'grabbing',
                    opacity: 0.5,
                  }}
                />
          ) : null}
            </DragOverlay>
          </DndContext>
        </section>
      </div>
      {isCreateModalOpened && (
        <CreateNoteTemplateModal
          open={isCreateModalOpened}
          onClose={() => setCreateModalOpened(false)}
          onCreate={handleCreateNote}
        />
      )}
      {templateModalOpened && (
        <CreateFolderModal
          templateModalOpened={templateModalOpened}
          setTemplateModalOpened={setTemplateModalOpened}
          createFolder={createNoteTemplateFolder}
        />
      )}
    </>
  );
};

export default NoteTemplates;
